import React, { useState } from "react";
import {
  FileText,
  Mail,
  Copy,
  Check as CheckIcon,
  Download,
  RotateCcw,
  AlertTriangle,
  Sparkles,
  Check,
  ListChecks,
  ClipboardList,
} from "lucide-react";
import { BACKEND_URL } from "@/lib/constants";
import { ScoreRing } from "./ScoreRing";
import { CVDocument, CoverLetterDocument } from "./CVDocument";

export function ResultsScreen({ t, result, onRestart }) {
  const [tab, setTab] = useState("cv");
  const [view, setView] = useState("document"); // document | plain
  const [copied, setCopied] = useState(false);

  const dl = result.downloads || {};
  // Build absolute URLs from relative paths returned by backend
  const absUrl = (rel) => (rel?.startsWith("http") ? rel : `${BACKEND_URL}${rel}`);

  const copyText = async () => {
    const text = tab === "cv" ? result.optimized_cv_text : result.cover_letter;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const delta = result.score_after - result.score_before;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="results-screen">
      <div className="mb-10">
        <div className="overline mb-3">{t.results.title}</div>
        <h2 className="font-heading font-bold text-4xl lg:text-5xl text-[#292524] leading-tight mb-2">
          {t.results.title}
        </h2>
        <p className="text-[#57534E] text-lg">{t.results.sub}</p>
      </div>

      {/* Score row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-10">
        <div className="card flex items-center gap-6" data-testid="score-before-card">
          <ScoreRing value={result.score_before} label={t.results.before} color="#E5A93D" />
          <div>
            <div className="overline text-[#D9423E]">Before</div>
            <div className="font-heading font-bold text-2xl text-[#292524]">{t.results.score_label}</div>
            <div className="text-sm text-[#78716C] mt-1">ATS-risky</div>
          </div>
        </div>
        <div className="card flex items-center gap-6 border-[#D95D39] !border-2" data-testid="score-after-card">
          <ScoreRing value={result.score_after} label={t.results.after} color="#3B8268" />
          <div>
            <div className="overline text-[#3B8268]">After</div>
            <div className="font-heading font-bold text-2xl text-[#292524]">{t.results.score_label}</div>
            <div className="text-sm text-[#78716C] mt-1">ATS-ready</div>
          </div>
        </div>
        <div className="card flex flex-col justify-center" data-testid="score-delta-card">
          <div className="overline mb-2">Boost</div>
          <div className="font-heading font-extrabold text-5xl text-[#D95D39] leading-none">
            +{delta}
          </div>
          <div className="text-sm text-[#78716C] mt-2">points ATS</div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              className={`tag cursor-pointer ${tab === "cv" ? "tag-brand" : ""}`}
              onClick={() => setTab("cv")}
              data-testid="tab-cv"
            >
              <FileText className="w-3.5 h-3.5" /> {t.results.cv_tab}
            </button>
            <button
              className={`tag cursor-pointer ${tab === "cover" ? "tag-brand" : ""}`}
              onClick={() => setTab("cover")}
              data-testid="tab-cover"
            >
              <Mail className="w-3.5 h-3.5" /> {t.results.cover_tab}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <div className="inline-flex bg-white border border-[#EAE5DB] rounded-xl p-1" data-testid="view-toggle">
                <button
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${view === "document" ? "bg-[#292524] text-white" : "text-[#57534E]"}`}
                  onClick={() => setView("document")}
                  data-testid="view-document-btn"
                >
                  Document
                </button>
                <button
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${view === "plain" ? "bg-[#292524] text-white" : "text-[#57534E]"}`}
                  onClick={() => setView("plain")}
                  data-testid="view-plain-btn"
                >
                  Plain text
                </button>
              </div>
              <button className="tag cursor-pointer" onClick={copyText} data-testid="copy-btn">
                {copied ? <CheckIcon className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? t.results.copied : t.results.copy}
              </button>
            </div>
          </div>

          {/* Styled doc OR plain text */}
          {view === "document" ? (
            tab === "cv" ? <CVDocument text={result.optimized_cv_text} /> : <CoverLetterDocument text={result.cover_letter} />
          ) : (
            <div className="cv-preview" data-testid="cv-preview">
              {tab === "cv" ? result.optimized_cv_text : result.cover_letter}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <a
              className="btn-primary"
              href={absUrl(dl.cv_pdf)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-cv-pdf-btn"
            >
              <Download className="w-4 h-4" /> {t.results.download_pdf}
            </a>
            <a
              className="btn-outline"
              href={absUrl(dl.cv_docx)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-cv-docx-btn"
            >
              <Download className="w-4 h-4" /> {t.results.download_docx}
            </a>
            <a
              className="btn-secondary"
              href={absUrl(dl.cover_pdf)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-cover-btn"
            >
              <Download className="w-4 h-4" /> {t.results.download_cover}
            </a>
            {dl.action_plan_pdf && (
              <a
                className="btn-outline !border-[#2A5C5A] !text-[#2A5C5A] hover:!bg-[#2A5C5A] hover:!text-white"
                href={absUrl(dl.action_plan_pdf)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-action-plan-btn"
              >
                <ClipboardList className="w-4 h-4" /> {t.results.download_action_plan}
              </a>
            )}
            <button className="btn-outline ml-auto" onClick={onRestart} data-testid="restart-btn">
              <RotateCcw className="w-4 h-4" /> {t.results.redo}
            </button>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-4">
          {result.issues_detected?.length > 0 && (
            <div className="card" data-testid="issues-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#D9423E]" />
                <div className="font-heading font-semibold text-[#292524]">{t.results.issues}</div>
              </div>
              <ul className="space-y-2 text-sm text-[#57534E]">
                {result.issues_detected.map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#D9423E] mt-0.5">•</span> <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.improvements_made?.length > 0 && (
            <div className="card" data-testid="improvements-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#3B8268]" />
                <div className="font-heading font-semibold text-[#292524]">{t.results.improvements}</div>
              </div>
              <ul className="space-y-2 text-sm text-[#57534E]">
                {result.improvements_made.map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <Check className="w-3.5 h-3.5 text-[#3B8268] mt-1 flex-shrink-0" />{" "}
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.missing_critical?.length > 0 && (
            <div className="card !border-[#E5A93D] !bg-[#FFF8EB]" data-testid="missing-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#E5A93D]" />
                <div className="font-heading font-semibold text-[#292524]">{t.results.missing}</div>
              </div>
              <ul className="space-y-2 text-sm text-[#57534E]">
                {result.missing_critical.map((x, i) => (
                  <li key={i}>• {x}</li>
                ))}
              </ul>
            </div>
          )}

          {result.checklist?.length > 0 && (
            <div className="card" data-testid="checklist-card">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="w-4 h-4 text-[#D95D39]" />
                <div className="font-heading font-semibold text-[#292524]">{t.results.checklist}</div>
              </div>
              <ul className="space-y-2 text-sm text-[#57534E]">
                {result.checklist.map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#D95D39] mt-0.5">→</span> <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
